import React, { useCallback, useEffect } from "react";
import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { SHOPPER_DETAIL } from "../../../actions/admin/customer-actions";
import Loader from "../../../components/common/loader/Loader";
import { formatName } from "../../../utils/common";
import {
  formatBooleanLabel,
  formatDateTime,
  formatPersonName,
  resolveFormattedDisplayName,
} from "../customer-management/customerDetailUtils";
import {
  StyledAvatarCircle,
  StyledBackButton,
  StyledBadgeDot,
  StyledBadgeRow,
  StyledContactIcon,
  StyledContactRow,
  StyledContactText,
  StyledDetailCard,
  StyledDetailCardContent,
  StyledEmptyStateText,
  StyledErrorPage,
  StyledErrorTitle,
  StyledFieldIcon,
  StyledFieldLabel,
  StyledFieldValue,
  StyledHeaderCard,
  StyledHeroContent,
  StyledHeroName,
  StyledHeroRow,
  StyledLoaderWrap,
  StyledMemberSinceHighlight,
  StyledOutlineBadge,
  StyledPageRoot,
  StyledProfileFieldLabelRow,
  StyledProfileFieldRoot,
  StyledRetryButton,
  StyledSectionHeadingRow,
  StyledSectionIconBox,
  StyledSectionTitle,
  StyledStatTile,
  StyledStatTileLabel,
  StyledStatTilesRow,
  StyledStatTileValue,
  StyledTableScroll,
} from "./style";

const listPath = "/admin/shoppers";

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return (parts[0]?.slice(0, 2) || "—").toUpperCase();
}

function OutlineBadge({ label, variant = "success" }) {
  return (
    <StyledOutlineBadge component="span" variant={variant}>
      <StyledBadgeDot component="span" variant={variant} />
      {label}
    </StyledOutlineBadge>
  );
}

function ProfileField({ label, value, monospace = false, icon: Icon }) {
  const display = value == null || value === "" ? "—" : value;
  return (
    <StyledProfileFieldRoot>
      <StyledProfileFieldLabelRow>
        {Icon && (
          <StyledFieldIcon as={Icon} />
        )}
        <StyledFieldLabel>{label}</StyledFieldLabel>
      </StyledProfileFieldLabelRow>
      <StyledFieldValue monospace={monospace} indented={Boolean(Icon)}>
        {display}
      </StyledFieldValue>
    </StyledProfileFieldRoot>
  );
}

function StatTile({ label, value, variant = "default" }) {
  return (
    <StyledStatTile variant={variant}>
      <StyledStatTileLabel>{label}</StyledStatTileLabel>
      <StyledStatTileValue variant={variant}>{value}</StyledStatTileValue>
    </StyledStatTile>
  );
}

function SectionHeading({ title, icon: Icon }) {
  return (
    <StyledSectionHeadingRow>
      {Icon && (
        <StyledSectionIconBox>
          <Icon />
        </StyledSectionIconBox>
      )}
      <StyledSectionTitle>{title}</StyledSectionTitle>
    </StyledSectionHeadingRow>
  );
}

export default function ShopperDetailPage() {
  const { shopperId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { shopperDetail, shopperDetailLoading, shopperDetailError } = useSelector(
    (state) => state?.shopper ?? {}
  );

  const fetchDetail = useCallback(() => {
    if (!shopperId) return;
    dispatch({ type: SHOPPER_DETAIL, payload: { id: shopperId } });
  }, [dispatch, shopperId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const shopper = shopperDetail;
  const displayName = resolveFormattedDisplayName(shopper);
  const firstName = formatPersonName(shopper?.first_name);
  const lastName = formatPersonName(shopper?.last_name);
  const assignedItems = Array.isArray(shopper?.personal_shopper_items)
    ? shopper.personal_shopper_items
    : [];
  const summary = shopper?.assigned_items_summary || {};

  const statusLabel = formatBooleanLabel(shopper?.is_suspended, {
    trueLabel: "Suspended",
    falseLabel: "Active",
  });
  const statusVariant = shopper?.is_suspended ? "danger" : "success";

  const verifyLabel = formatBooleanLabel(shopper?.is_email_verified, {
    trueLabel: "Verified",
    falseLabel: "Not verified",
  });
  const verifyVariant = shopper?.is_email_verified ? "success" : "danger";

  const profileLabel = formatBooleanLabel(shopper?.is_profile_completed, {
    trueLabel: "Completed",
    falseLabel: "Incomplete",
  });

  if (shopperDetailLoading && !shopper) {
    return (
      <StyledLoaderWrap>
        <Loader minHeight={320} size={48} />
      </StyledLoaderWrap>
    );
  }

  if (!shopper && shopperDetailError) {
    return (
      <StyledErrorPage>
        <StyledBackButton
          startIcon={<ChevronLeftIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to shoppers
        </StyledBackButton>
        <StyledErrorTitle color="error">
          Shopper not found or could not be loaded.
        </StyledErrorTitle>
        <Typography variant="body2" color="text.secondary">
          {shopperDetailError}
        </Typography>
        <StyledRetryButton variant="outlined" onClick={fetchDetail}>
          Retry
        </StyledRetryButton>
      </StyledErrorPage>
    );
  }

  if (!shopper) {
    return null;
  }

  return (
    <StyledPageRoot>
      <StyledBackButton
        size="small"
        startIcon={<ChevronLeftIcon />}
        onClick={() => navigate(listPath)}
      >
        Back to shoppers
      </StyledBackButton>

      <StyledHeaderCard elevation={0}>
        <StyledDetailCardContent>
          <StyledHeroRow>
            <StyledAvatarCircle>{getInitials(displayName)}</StyledAvatarCircle>
            <StyledHeroContent>
              <StyledHeroName>{displayName}</StyledHeroName>
              {shopper.email && (
                <StyledContactRow>
                  <StyledContactIcon as={EmailOutlinedIcon} />
                  <StyledContactText>{shopper.email}</StyledContactText>
                </StyledContactRow>
              )}
              {shopper.phone_number && (
                <StyledContactRow dense>
                  <StyledContactIcon as={PhoneOutlinedIcon} />
                  <StyledContactText>{shopper.phone_number}</StyledContactText>
                </StyledContactRow>
              )}
              <StyledBadgeRow>
                <OutlineBadge label={statusLabel} variant={statusVariant} />
                <OutlineBadge label={verifyLabel} variant={verifyVariant} />
              </StyledBadgeRow>
              {shopper.createdAt && (
                <StyledContactRow>
                  <StyledContactIcon as={CalendarTodayOutlinedIcon} />
                  <StyledContactText>
                    Member since{" "}
                    <StyledMemberSinceHighlight component="span">
                      {formatDateTime(shopper.createdAt)}
                    </StyledMemberSinceHighlight>
                  </StyledContactText>
                </StyledContactRow>
              )}
            </StyledHeroContent>
          </StyledHeroRow>
        </StyledDetailCardContent>
      </StyledHeaderCard>

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 5 }}>
          <StyledDetailCard elevation={0}>
            <StyledDetailCardContent>
              <SectionHeading title="Profile details" icon={PersonOutlineIcon} />
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ProfileField label="First name" value={firstName} icon={PersonOutlineIcon} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ProfileField label="Last name" value={lastName} icon={PersonOutlineIcon} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ProfileField
                    label="Alternative phone"
                    value={shopper.alternative_phone_number}
                    icon={PhoneOutlinedIcon}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ProfileField label="Profile completed" value={profileLabel} icon={PersonOutlineIcon} />
                </Grid>
              </Grid>
            </StyledDetailCardContent>
          </StyledDetailCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <StyledDetailCard elevation={0}>
            <StyledDetailCardContent>
              <SectionHeading title="Assigned work" icon={ShoppingBagOutlinedIcon} />
              <StyledStatTilesRow>
                <StatTile label="Total assigned" value={summary.total ?? assignedItems.length} />
                <StatTile label="Approved" value={summary.approved ?? 0} variant="success" />
                <StatTile label="Pending" value={summary.pending ?? 0} variant="warning" />
                <StatTile label="In progress" value={summary.in_progress ?? 0} variant="warning" />
              </StyledStatTilesRow>
            </StyledDetailCardContent>
          </StyledDetailCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <StyledDetailCard elevation={0}>
            <StyledDetailCardContent>
              <SectionHeading title="Assigned products" icon={ShoppingBagOutlinedIcon} />
              {assignedItems.length === 0 ? (
                <StyledEmptyStateText>
                  No products assigned to this shopper yet.
                </StyledEmptyStateText>
              ) : (
                <StyledTableScroll>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell>Assigned on</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignedItems.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>{item.product_name || "—"}</TableCell>
                          <TableCell>{formatName(item.status) || "—"}</TableCell>
                          <TableCell>{formatName(item.payment_status) || "—"}</TableCell>
                          <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                          <TableCell align="right">
                            <Link
                              component={RouterLink}
                              to={`/admin/items/${item.id}`}
                              underline="hover"
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableScroll>
              )}
            </StyledDetailCardContent>
          </StyledDetailCard>
        </Grid>
      </Grid>
    </StyledPageRoot>
  );
}
